import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import FormField from "./form-field";
import { authClient } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const [email, setEmail] = useState("");
  const [isSendingLink, setIsSendingLink] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate(); // Keep navigate for potential future use or if still needed
  const { t } = useTranslation();

  const handleMagicLinkSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    setIsSendingLink(true);
    setError(null);
    try {
      const { error: magicLinkError } = await authClient.signIn.magicLink({
        email,
        callbackURL: `${window.location.origin}/dashboard`, // Or a more specific callback page
      });

      if (magicLinkError) {
        setError(magicLinkError.message || t("loginForm.magicLinkError"));
        toast.error(magicLinkError.message || t("loginForm.magicLinkError"));
      } else {
        toast.success(t("loginForm.magicLinkSuccess"));
        // Optionally, clear the email field or show a message to check email
        // setEmail(""); // Uncomment if you want to clear the field
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast.error(message);
    } finally {
      setIsSendingLink(false);
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle>{t("loginForm.magicLinkTitle")}</CardTitle>
          <CardDescription>
            {t("loginForm.magicLinkDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleMagicLinkSubmit}>
            <div className="flex flex-col gap-4">
              <FormField
                label={t("common.email")}
                id="email"
                type="email" // Changed to email type
                placeholder={t("common.emailPlaceholder")}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSendingLink}
              />
              {error && (
                <p className="mt-2 text-sm text-destructive">{error}</p>
              )}
              <div className="flex flex-col gap-3">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSendingLink}
                >
                  {isSendingLink
                    ? t("loginForm.sendingLink")
                    : t("loginForm.sendMagicLink")}
                </Button>
              </div>
            </div>
            {/* Registration link can be removed if magic link is the only way */}
            {/* Or it can point to the same login form if preferred */}
            <div className="mt-4 text-center text-sm">
              {t("loginForm.noAccountPrompt")}{" "}
              <span className="font-medium">
                {t("loginForm.magicLinkInfo")}
              </span>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
